import { Box, Button, Stack, Typography, Paper, CircularProgress, TextField, Grid } from '@mui/material';
import CustomSnackbar from 'components/CustomSnackbar';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import MDBox from '../../../components/MDBox';
import MDTypography from '../../../components/MDTypography';
import DashboardNavbar from '../../../examples/Navbars/DashboardNavbar';
import DashboardLayout from '../../LayoutContainers/DashboardLayout';
import { fetchControllers as fetchDevices, saveSchedule } from '../../../stores/sensor/controllersSlice';
import { getBuildings } from '../../../stores/sensor/sensorStore';
import DevicesListFilter from 'components/DevicesFilter/DevicesListFilter.js';
import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { filterDevices, getSensorOptions } from '../../../helpers/filterHelpers';
import EditSchedulePatternsContainer from './EditSchedulePatternsContainer';

const NewScheduleForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [scheduleName, setScheduleName] = useState('');
  const [localSelectedControllers, setLocalSelectedControllers] = useState([]);
  const scheduleTableRef = useRef(null);
  const [snack, setSnack] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [changedFlags, setChangedFlags] = useState({
    patterns: false,
    metadata: false,
    devices: false,
  });

  const sectionWidth = { xs: '100%', lg: '85%', xl: '70%' };
  const devices = useSelector(s => s.controllers.controllers || []);
  const { selectedFilterObject } = useSelector(s => s.persistence);
  const searchString = useSelector(s => s.persistence.searchString || '');
  const currentUser = useSelector(s => s.sensors.currentUser);
  const { distributor, customer, building, floor, area, status } = selectedFilterObject || {};
  const [isInitializing, setIsInitializing] = useState(true);

  // Filtered controllers
  const filteredDevices = useMemo(() => {
    if (devices.length === 0) return [];
    return filterDevices(
      devices,
      {
        distributor,
        customer,
        building,
        floor,
        area,
        status,
        searchString,
      },
      currentUser,
    ).filter(d => d.device_type?.startsWith('Controller'));
  }, [devices, distributor, customer, building, floor, area, status, searchString, currentUser]);

  // Available controller IDs
  const availableControllersIds = useMemo(
    () =>
      (filteredDevices || [])
        .map(d => d.id)
        .filter(Boolean)
        .sort(),
    [filteredDevices],
  );

  const sensorOptions = useMemo(() => getSensorOptions(filteredDevices), [filteredDevices]);
  const selectedDevicesForPresetFiltering = useMemo(() => {
    const devicesById = new Map(devices.map(device => [String(device.id), device]));
    return localSelectedControllers.map(deviceId => devicesById.get(String(deviceId))).filter(Boolean);
  }, [devices, localSelectedControllers]);

  // Change markers
  const markPatternsChanged = useCallback(() => {
    setChangedFlags(prev => ({ ...prev, patterns: true }));
  }, []);

  const markMetadataChanged = useCallback(() => {
    setChangedFlags(prev => ({ ...prev, metadata: true }));
  }, []);

  const markDevicesChanged = useCallback(() => {
    setChangedFlags(prev => ({ ...prev, devices: true }));
  }, []);

  // Handlers
  const handleControllerChange = useCallback(selectedControllerIds => {
    setLocalSelectedControllers(selectedControllerIds);
    markDevicesChanged();
  }, [markDevicesChanged]);

  const handleScheduleNameChange = useCallback(
    e => {
      setScheduleName(e.target.value);
      markMetadataChanged();
    },
    [markMetadataChanged],
  );

  const handleSearchClick = useCallback(() => {}, []);

  const handleSaveSchedule = useCallback(async () => {
    // Validation
    if (!scheduleName.trim()) {
      setSnack({
        open: true,
        message: 'Schedule name is required',
        severity: 'error',
      });
      return;
    }

    if (localSelectedControllers.length === 0) {
      setSnack({
        open: true,
        message: 'At least one device must be selected',
        severity: 'error',
      });
      return;
    }

    const patterns = scheduleTableRef.current?.getPatterns?.() ?? [];
    if (!patterns.length) {
      setSnack({
        open: true,
        message: 'Schedule patterns are required',
        severity: 'error',
      });
      return;
    }

    const payload = {
      scheduleData: {
        schedule_name: scheduleName.trim(),
        is_global: true,
      },
      patterns,
      deviceIds: localSelectedControllers,
    };

    setIsSaving(true);
    try {
      await dispatch(saveSchedule(payload)).unwrap();
      setSnack({
        open: true,
        message: 'Schedule created successfully',
        severity: 'success',
      });
      // Redirect after brief delay to show success message
      setTimeout(() => navigate('/manage-schedules'), 1000);
    } catch (error) {
      setSnack({
        open: true,
        message: `Failed to create schedule: ${error.message || 'Unknown error'}`,
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  }, [dispatch, localSelectedControllers, scheduleName, navigate]);

  // Initialize data on component mount
  useEffect(() => {
    const initData = async () => {
      setIsInitializing(true);
      try {
        await Promise.all([dispatch(fetchDevices('Controller')), dispatch(getBuildings())]);
      } finally {
        setIsInitializing(false);
      }
    };

    initData();
  }, [dispatch]);

  const canSave = changedFlags.patterns && changedFlags.metadata && changedFlags.devices;

  return (
    <DashboardLayout>
      <DashboardNavbar objectName="New schedule" />
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
        {/* Header */}
        <MDBox
          my={1}
          px={2}
          py={2}
          variant="gradient"
          style={{
            background: '#3244E2',
            width: '100%',
          }}
          borderRadius="lg"
          coloredShadow="info">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" marginLeft={2} spacing={2} alignItems="end">
              <MDTypography variant="h4" color="light">
                Create New Schedule
              </MDTypography>
            </Stack>
          </Stack>
        </MDBox>

        {/* Schedule Editor - Full Width at Top */}
        <Paper
          elevation={1}
          sx={{
            p: '1.5rem',
            borderRadius: '1rem',
            backgroundColor: '#fff',
            width: sectionWidth,
            alignSelf: 'center',
          }}>
          <Typography variant="h4" sx={{ mb: 2 }}>
            Schedule Editor
          </Typography>
          <Box sx={{ mt: 1 }}>
            <EditSchedulePatternsContainer
              scheduleTableRef={scheduleTableRef}
              onPatternsChange={markPatternsChanged}
              isRecommendation={false}
              devices={selectedDevicesForPresetFiltering}
            />
          </Box>
        </Paper>

        {/* Two-Column Section */}
        <Box
          sx={{
            width: sectionWidth,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3,
          }}>
          {/* Schedule Properties */}
          <Paper
            elevation={1}
            sx={{
              borderRadius: '1rem',
              flex: 1,
              width: '100%',
              padding: '1.5rem',
              display: 'flex',
              flexDirection: 'column',
            }}>
            <Typography variant="h4" sx={{ mb: 2 }}>
              Schedule Properties
            </Typography>
            <Grid container spacing={2} py={1.5}>
              <Grid item xs={12}>
                <TextField
                  label="Schedule Name"
                  name="schedule_name"
                  value={scheduleName}
                  onChange={handleScheduleNameChange}
                  fullWidth
                  required
                  placeholder="Enter schedule name"
                />
              </Grid>
              {/* <Grid item xs={12}>
                <Box
                  sx={{
                    border: theme => `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    px: 2,
                    py: 1.5,
                    backgroundColor: 'background.default',
                  }}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    Pending Creation
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    border: theme => `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    px: 2,
                    py: 1.5,
                    backgroundColor: 'background.default',
                  }}>
                  <Typography variant="caption" color="text.secondary">
                    Generated Timestamp
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {new Date().toLocaleString()}
                  </Typography>
                </Box>
              </Grid> */}
            </Grid>
          </Paper>

          {/* Device Selection */}
          <Paper
            elevation={1}
            sx={{
              borderRadius: '1rem',
              flex: 1,
              width: '100%',
              padding: '1.5rem',
              display: 'flex',
              flexDirection: 'column',
            }}>
            <Typography variant="h4" sx={{ mb: 2 }}>
              Device Selection
            </Typography>
            <DevicesListFilter
              status={false}
              search={false}
              sensorsSelect={true}
              selectedSensors={localSelectedControllers}
              onSensorChange={handleControllerChange}
              sensorOptions={sensorOptions}
              showSearchButton={false}
              onSearchClick={handleSearchClick}
              flexDirection="column"
            />
          </Paper>
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            margin: {
              xs: '0px 0px',
              md: '0px 10rem',
              lg: '0px 22rem',
              xl: '0px 30rem',
              xxl: '0px 42rem',
            },
            display: 'flex',
            flexDirection: 'column',
          }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveSchedule}
            disabled={!canSave || isSaving}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}>
            {isSaving ? 'Creating Schedule...' : 'CREATE SCHEDULE'}
          </Button>
          {/* Back Button */}
          <Button variant="text" onClick={() => navigate('/manage-schedules')} sx={{ mt: 1 }}>
            ← Back to Schedules List
          </Button>
        </Box>
      </Box>

      <CustomSnackbar
        {...snack}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        duration={3000}
        vertical="top"
        horizontal="left"
        color="#fff"
      />
    </DashboardLayout>
  );
};

export default NewScheduleForm;
